// ============================================================
// SWT Application Constants
// All numeric codes match SSM Cloud API / database values
// ============================================================

export const WT_STATUS = {
  PENDING: 588,
  IN_PROGRESS: 589,
  COMPLETED: 590,
} as const;

export const WO_STATUS = {
  NEW: 661,
  IN_PROGRESS: 662,
  COMPLETED: 663,
  CLOSED: 664,
} as const;

export const WO_PRIORITY = {
  NORMAL: 227,
  MEDIUM: 228,
  HIGH: 229,
} as const;

export const UNIT_STATUS = {
  VACANT: 192,
  UNAVAILABLE: 193,
  COMPANY: 194,
  RENTED: 195,
  RESERVED: 196,
  PENDING_MOVEIN: 197,
} as const;

/** Physical condition of the unit — stored as unw_int_UnitPhysicalStatusId */
export const UNIT_PHYSICAL_STATUS = {
  GOOD: 1,
  FAIR: 2,
  POOR: 3,
  DAMAGED: 4,
} as const;

export const UNIT_PHYSICAL_STATUS_LABELS: Record<number, string> = {
  [UNIT_PHYSICAL_STATUS.GOOD]: 'Good',
  [UNIT_PHYSICAL_STATUS.FAIR]: 'Fair',
  [UNIT_PHYSICAL_STATUS.POOR]: 'Poor',
  [UNIT_PHYSICAL_STATUS.DAMAGED]: 'Damaged',
};

export const WO_TYPE = {
  UNIT: 1,
  BUILDING: 2,
} as const;

export const BLD_CATEGORY = {
  INTERIOR: 850,
  EXTERIOR: 851,
  SECURITY: 852,
  OTHER: 853,
} as const;

export const BLD_CATEGORY_LABELS: Record<number, string> = {
  [BLD_CATEGORY.INTERIOR]: 'Interior',
  [BLD_CATEGORY.EXTERIOR]: 'Exterior',
  [BLD_CATEGORY.SECURITY]: 'Security',
  [BLD_CATEGORY.OTHER]: 'Other',
};

export const WO_STATUS_LABELS: Record<number, string> = {
  [WO_STATUS.NEW]: 'New',
  [WO_STATUS.IN_PROGRESS]: 'In Progress',
  [WO_STATUS.COMPLETED]: 'Completed',
  [WO_STATUS.CLOSED]: 'Closed',
};

export const WO_PRIORITY_LABELS: Record<number, string> = {
  [WO_PRIORITY.NORMAL]: 'Normal',
  [WO_PRIORITY.MEDIUM]: 'Medium',
  [WO_PRIORITY.HIGH]: 'High',
};

export const UNIT_STATUS_LABELS: Record<number, string> = {
  [UNIT_STATUS.VACANT]: 'Vacant',
  [UNIT_STATUS.UNAVAILABLE]: 'Unavailable',
  [UNIT_STATUS.COMPANY]: 'Company Unit',
  [UNIT_STATUS.RENTED]: 'Rented',
  [UNIT_STATUS.RESERVED]: 'Reserved',
  [UNIT_STATUS.PENDING_MOVEIN]: 'Pending Move-In',
};

/** Physical/occupancy status select options (used in unit walkthrough detail) */
export const WALKTHROUGH_STATUS_OPTIONS = [
  { value: 0, label: '— Unchanged —' },
  { value: UNIT_STATUS.VACANT, label: 'Vacant' },
  { value: UNIT_STATUS.UNAVAILABLE, label: 'Unavailable' },
  { value: UNIT_STATUS.COMPANY, label: 'Company Unit' },
  { value: UNIT_STATUS.RENTED, label: 'Rented' },
  { value: UNIT_STATUS.RESERVED, label: 'Reserved' },
  { value: UNIT_STATUS.PENDING_MOVEIN, label: 'Pending Move-In' },
];

/** Physical condition select options (used in unit walkthrough detail) */
export const PHYSICAL_COND_OPTIONS = [
  { value: 0, label: '— Select —' },
  { value: UNIT_PHYSICAL_STATUS.GOOD, label: 'Good' },
  { value: UNIT_PHYSICAL_STATUS.FAIR, label: 'Fair' },
  { value: UNIT_PHYSICAL_STATUS.POOR, label: 'Poor' },
  { value: UNIT_PHYSICAL_STATUS.DAMAGED, label: 'Damaged' },
];

export const WT_STATUS_LABELS: Record<number, string> = {
  [WT_STATUS.PENDING]: 'Pending',
  [WT_STATUS.IN_PROGRESS]: 'In Progress',
  [WT_STATUS.COMPLETED]: 'Completed',
};

/** StorageTreasures auction category options (multi-select) */
export const AUCTION_CATEGORIES: string[] = [
  'Appliances',
  'Arms',
  'Books',
  'Boxes',
  'Cabinets & Shelves',
  'Clothing & Shoes',
  'Collectibles',
  'Computers',
  'Dishes & Kitchenware',
  'Documents & Files',
  'Electronics',
  'Food & Beverages',
  'Health & Wellness',
  'Heavy Equipment',
  'Household Furniture',
  'Jewelry',
  'Lamps',
  'Mattress & Bedding',
  'Memorabilia',
  'Mirrors',
  'Motor Vehicles & Parts',
  'Movies, Music & Books',
  'New Merchandise',
  'Office Equipment',
  'Old Stuff',
  'Personal Effects',
  'Sports & Outdoors',
  'Tools & Supplies',
  'Toys, Baby & Games',
  'Wall Art',
];

/** Rolling pull window (days) used for post-upload pulls once a sync tracker entry exists. */
export const SYNC_WINDOW_DAYS = 7;

/** Initial hydration window (days) used on the very first pull when no sync tracker entry exists. */
export const INITIAL_SYNC_DAYS = 30;

// ============================================================
// IndexedDB Configuration
// ============================================================

/** SWT offline database name */
export const SWT_IDB_NAME = 'SWT_IDB';

/** Increment this when the schema changes (new stores / indexes) */
export const SWT_IDB_VERSION = 22;

/** Object-store names — keep in sync with SwtIndexedDbService upgrade logic */
export const SWT_STORE = {
  USERS: 'swt_users',
  TOKENS: 'swt_tokens',
  SETTINGS: 'swt_settings',
  BUILDING_AREAS: 'swt_building_areas',
  UNITS_LIST: 'swt_units_list',
  WALKTHROUGHS: 'swt_walkthroughs',
  UNIT_WALKTHROUGHS: 'swt_unit_walkthroughs',
  /** New UnitWalkThrough header store (maps to dbo.UnitWalkThrough) */
  UWT: 'swt_uwt',
  /** New FacilityUnitWalkThroughDetails store (maps to dbo.FacilityUnitWalkThroughDetails) */
  FUWTD: 'swt_fuwtd',  /** Walkthrough notes trail — one row per note entry, keyed by code */
  WALKTHROUGH_NOTES: 'swt_walkthrough_notes',
  /** Walkthrough list summary — one row per walkthrough session (v7) */
  WALKTHROUGH_LIST: 'swt_walkthrough_list',
  /** Auction details per unit — one row per unit per walkthrough (v10) */
  AUCTION_DETAILS: 'swt_auction_details',
  /** Work orders list — one row per WO (v11) */
  WORK_ORDERS: 'swt_work_orders',
  /** Building walkthrough detail rows snapshot — one row per unit/section per walkthrough (v11) */
  BLD_WT_ROWS: 'swt_bld_wt_rows',
  /** Building walkthrough header — mirrors dbo.BuildingWalkThrough (v12) */
  BWT: 'swt_bwt',
  /** Building walkthrough details — mirrors dbo.BuildingWalkThroughDetails (v12) */
  BWTD: 'swt_bwtd',
  /** Unit walkthrough photos — mirrors dbo.SiteWalkThroughAttachments for unit rows (v13) */
  UNIT_WT_PHOTOS: 'swt_unit_wt_photos',
  /** Sync tracker — one row per domain table, records last successful sync timestamp (v14) */
  SYNC_TRACKER: 'swt_sync_tracker',
  /** Building walkthrough photos — one row per photo per area per walkthrough (v15) */
  BWT_PHOTOS: 'swt_bwt_photos',
  /** Work order photos — one row per photo per work order (v16) */
  WO_PHOTOS: 'swt_work_order_photos',
  /** Scanner scan history — one row per scan, keyed by facility/user/date (v20) */
  SCAN_HISTORY: 'swt_scan_history',
  /** Auction photos — one row per photo per unit per walkthrough (v21) */
  AUC_PHOTOS: 'swt_auc_photos',
  /** Pending auction saves queued while offline (v22) */
  AUC_PENDING: 'swt_auction_pending',
} as const;

export type SwtStoreName = typeof SWT_STORE[keyof typeof SWT_STORE];

/**
 * UTC bounds of the browser-local calendar day containing `iso` (default: now).
 * Built from local midnight → next local midnight, so the range is 23/25 hours on
 * DST transition days rather than a fixed 24. Sent with SaveUnitWalkThrough so the
 * server's same-day header match uses the user's local day, not the UTC day.
 */
export function localDayUtcRange(iso?: string): { localDayStartUtc: string; localDayEndUtc: string } {
  const d = iso ? new Date(iso) : new Date();
  const ref = isNaN(d.getTime()) ? new Date() : d;
  const start = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate());
  const end = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate() + 1);
  return { localDayStartUtc: start.toISOString(), localDayEndUtc: end.toISOString() };
}
